import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import UnsavedCloseConfirm from '../pointItem/UnsavedCloseConfirm';
import type { FlatMember } from './GeneralAssignmentSettingsModal';
import type {
  AssignmentPointsNoteDoc,
  AssignmentStatusValue,
  AssignmentStudentStatus,
  GeneralAssignmentItem,
  ProjectAssignmentItem,
  RoutineAssignmentItem,
} from '../../types/assignmentPointsNote';
import {
  calculateRoutineDates,
  ensureRoutineDateBuckets,
  pointsForStatus,
  upsertStatus,
} from '../../utils/assignmentPointsNoteProgress';
import { INCOMPLETE_ROUTINE_BADGE_BG } from '../../utils/assignmentIncompleteReport';
import { displayClassMemberName } from '../../utils/classMemberNameDisplay';

type FilterTab = 'allIncomplete' | 'general' | 'project' | 'routine';
type ListFilter = 'all' | 'incomplete';

type TableColumn = {
  key: string;
  kind: 'general' | 'project' | 'routine';
  itemId: string;
  subTaskId?: string;
  date?: string;
  title: string;
  projectTitle?: string;
  subTaskTitle?: string;
  dueDate: string;
  color: string;
  basePoints: number;
  assignedMemberIds: string[];
  projectCreatedAt?: string;
  subTaskOrder?: number;
};

type PendingChange = {
  status: AssignmentStatusValue;
  points: number;
  memo?: string;
};

const STATUS_LEGEND: {
  value: AssignmentStatusValue;
  label: string;
  num: number;
  color: string;
  icon: string;
}[] = [
  { value: 'pending', label: '미확인', num: 1, color: '#FF316B', icon: 'X' },
  { value: 'confirmed', label: '확인', num: 2, color: '#227971', icon: 'V' },
  { value: 'delayed', label: '지연확인', num: 3, color: '#65A994', icon: '-' },
  { value: 'exempted', label: '제외', num: 4, color: '#FFCC6E', icon: '!' },
];

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
const COMPLETED: AssignmentStatusValue[] = ['confirmed', 'delayed', 'exempted'];

function toIsoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayIso(): string {
  return toIsoLocal(new Date());
}

/** 오늘 포함 최근 N일 (오늘이 인덱스 0) */
function recentDateIsos(n: number): string[] {
  const out: string[] = [];
  const base = new Date();
  for (let i = 0; i < n; i += 1) {
    const d = new Date(base);
    d.setDate(base.getDate() - i);
    out.push(toIsoLocal(d));
  }
  return out;
}

function formatHeaderDate(iso: string): { md: string; weekday: string } | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return {
    md: `${d.getMonth() + 1}/${d.getDate()}`,
    weekday: DAY_LABELS[d.getDay()] ?? '',
  };
}

function formatDateMenuLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const today = todayIso();
  if (iso === today) return '오늘';
  return `${d.getMonth() + 1}월 ${d.getDate()}일 (${DAY_LABELS[d.getDay()]})`;
}

function statusIcon(status: AssignmentStatusValue): string {
  const found = STATUS_LEGEND.find((s) => s.value === status);
  return found?.icon ?? 'X';
}

function statusFg(status: AssignmentStatusValue): string {
  return status === 'exempted' ? '#000000' : '#ffffff';
}

function isCompletedStatus(status: AssignmentStatusValue | undefined): boolean {
  return status != null && COMPLETED.includes(status);
}

function cellKey(memberId: string, colKey: string): string {
  return `${memberId}::${colKey}`;
}

function intersectIds(assigned: string[], groupIds: Set<string>): string[] {
  return assigned.filter((id) => groupIds.has(id));
}

function routineRunsOnDate(item: RoutineAssignmentItem, date: string): boolean {
  const schedule = calculateRoutineDates(item.startDate, item.dueDate, item.routineDays);
  if (schedule.includes(date)) return true;
  return item.dateStatuses.some((b) => b.date === date);
}

function readGeneralStatus(
  item: GeneralAssignmentItem,
  memberId: string
): AssignmentStudentStatus | undefined {
  return item.studentStatuses.find((s) => s.studentId === memberId);
}

function readProjectStatus(
  item: ProjectAssignmentItem,
  subTaskId: string,
  memberId: string
): AssignmentStudentStatus | undefined {
  return item.studentStatusesBySubTask[`${subTaskId}:${memberId}`];
}

function readRoutineStatus(
  item: RoutineAssignmentItem,
  date: string,
  memberId: string
): AssignmentStudentStatus | undefined {
  const ensured = ensureRoutineDateBuckets(item);
  const bucket = ensured.dateStatuses.find((b) => b.date === date);
  return bucket?.studentStatuses.find((s) => s.studentId === memberId);
}

function columnIsIncomplete(
  col: TableColumn,
  getStatus: (memberId: string, col: TableColumn) => AssignmentStatusValue
): boolean {
  const ids = col.assignedMemberIds;
  if (ids.length === 0) return false;
  return !ids.every((id) => isCompletedStatus(getStatus(id, col)));
}

type Props = {
  open: boolean;
  doc: AssignmentPointsNoteDoc;
  members: FlatMember[];
  onClose: () => void;
  onSave: (next: AssignmentPointsNoteDoc, touchedItemIds: string[]) => Promise<void>;
};

const AssignmentManagementTableModal: React.FC<Props> = ({
  open,
  doc,
  members,
  onClose,
  onSave,
}) => {
  const [filterTab, setFilterTab] = useState<FilterTab>('allIncomplete');
  const [listFilter, setListFilter] = useState<ListFilter>('all');
  const [categoryId, setCategoryId] = useState<string>('all');
  const [projectId, setProjectId] = useState<string>('all');
  const [routineDate, setRoutineDate] = useState<string>(() => todayIso());
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(() => new Set());
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(
    () => new Map()
  );
  const [unsavedConfirm, setUnsavedConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const pendingRef = useRef(pendingChanges);
  pendingRef.current = pendingChanges;
  const applyStatusRef = useRef<(status: AssignmentStatusValue) => void>(() => {});

  const groupIdSet = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const categories = doc.settings.categories ?? [];
  const hasChanges = pendingChanges.size > 0;

  useEffect(() => {
    if (!open) return;
    setFilterTab('allIncomplete');
    setListFilter('all');
    setCategoryId('all');
    setProjectId('all');
    setRoutineDate(todayIso());
    setFilterMenuOpen(false);
    setSelectedCells(new Set());
    setPendingChanges(new Map());
    setUnsavedConfirm(false);
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const resolveBaseStatus = useCallback(
    (memberId: string, col: TableColumn): AssignmentStudentStatus | undefined => {
      const item = doc.items.find((x) => x.id === col.itemId);
      if (!item) return undefined;
      if (col.kind === 'general' && item.kind === 'general') {
        return readGeneralStatus(item, memberId);
      }
      if (col.kind === 'project' && item.kind === 'project' && col.subTaskId) {
        return readProjectStatus(item, col.subTaskId, memberId);
      }
      if (col.kind === 'routine' && item.kind === 'routine' && col.date) {
        return readRoutineStatus(item, col.date, memberId);
      }
      return undefined;
    },
    [doc.items]
  );

  const resolveDisplayStatus = useCallback(
    (memberId: string, col: TableColumn): AssignmentStatusValue => {
      const ck = cellKey(memberId, col.key);
      const pending = pendingChanges.get(ck);
      if (pending) return pending.status;
      return resolveBaseStatus(memberId, col)?.status ?? 'pending';
    },
    [pendingChanges, resolveBaseStatus]
  );

  const buildColumns = useCallback((): TableColumn[] => {
    const cols: TableColumn[] = [];
    const catColor = (categoryId?: string) =>
      categories.find((c) => c.id === categoryId)?.color || '#a2a2a2';

    for (const item of doc.items) {
      if (item.kind === 'general') {
        const assigned = intersectIds(item.assignedMemberIds, groupIdSet);
        if (assigned.length === 0) continue;
        cols.push({
          key: `g:${item.id}`,
          kind: 'general',
          itemId: item.id,
          title: item.title || '제목 없음',
          dueDate: item.dueDate,
          color: catColor(item.categoryId),
          basePoints: item.basePoints,
          assignedMemberIds: assigned,
        });
      } else if (item.kind === 'project') {
        const assigned = intersectIds(item.assignedMemberIds, groupIdSet);
        if (assigned.length === 0) continue;
        const subs = [...item.subTasks].sort(
          (a, b) => (a.order || 0) - (b.order || 0)
        );
        for (const sub of subs) {
          cols.push({
            key: `p:${item.id}:${sub.id}`,
            kind: 'project',
            itemId: item.id,
            subTaskId: sub.id,
            title: sub.title || '하위과제',
            projectTitle: item.title || '제목 없음',
            subTaskTitle: sub.title || '하위과제',
            dueDate: item.dueDate,
            color: item.color || '#a2a2a2',
            basePoints: sub.points,
            assignedMemberIds: assigned,
            projectCreatedAt: item.createdAt,
            subTaskOrder: sub.order,
          });
        }
      } else if (item.kind === 'routine') {
        const assigned = intersectIds(item.assignedMemberIds, groupIdSet);
        if (assigned.length === 0) continue;
        // 루틴 컬럼은 선택 날짜(또는 미완료 전체=오늘) 기준 1개씩
      }
    }

    return cols;
  }, [categories, doc.items, groupIdSet]);

  const allBaseColumns = useMemo(() => buildColumns(), [buildColumns]);

  const projectOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of doc.items) {
      if (item.kind !== 'project') continue;
      if (!intersectIds(item.assignedMemberIds, groupIdSet).length) continue;
      map.set(item.id, item.title || '제목 없음');
    }
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }, [doc.items, groupIdSet]);

  const routineDateOptions = useMemo(() => recentDateIsos(8), []);

  const columns = useMemo(() => {
    const statusOf = (memberId: string, col: TableColumn): AssignmentStatusValue =>
      resolveBaseStatus(memberId, col)?.status ?? 'pending';

    let cols: TableColumn[] = [];

    if (filterTab === 'allIncomplete' || filterTab === 'general') {
      const generals = allBaseColumns.filter((c) => c.kind === 'general');
      cols.push(...generals);
    }
    if (filterTab === 'allIncomplete' || filterTab === 'project') {
      const projects = allBaseColumns.filter((c) => c.kind === 'project');
      cols.push(...projects);
    }
    if (filterTab === 'allIncomplete' || filterTab === 'routine') {
      const dateForRoutine =
        filterTab === 'allIncomplete' ? todayIso() : routineDate;
      for (const item of doc.items) {
        if (item.kind !== 'routine') continue;
        const assigned = intersectIds(item.assignedMemberIds, groupIdSet);
        if (assigned.length === 0) continue;
        if (!routineRunsOnDate(item, dateForRoutine)) continue;
        const cat =
          (item.categoryId
            ? categories.find((c) => c.id === item.categoryId)?.color
            : undefined) || INCOMPLETE_ROUTINE_BADGE_BG;
        cols.push({
          key: `r:${item.id}:${dateForRoutine}`,
          kind: 'routine',
          itemId: item.id,
          date: dateForRoutine,
          title: item.title || '제목 없음',
          dueDate: dateForRoutine,
          color: cat,
          basePoints: item.basePoints,
          assignedMemberIds: assigned,
        });
      }
    }

    if (filterTab === 'general') {
      if (categoryId !== 'all') {
        cols = cols.filter((c) => {
          if (c.kind !== 'general') return false;
          const item = doc.items.find((x) => x.id === c.itemId);
          return item?.kind === 'general' && item.categoryId === categoryId;
        });
      } else {
        cols = cols.filter((c) => c.kind === 'general');
      }
      if (listFilter === 'incomplete') {
        cols = cols.filter((c) => columnIsIncomplete(c, statusOf));
      }
    } else if (filterTab === 'project') {
      cols = cols.filter((c) => c.kind === 'project');
      if (projectId !== 'all') {
        cols = cols.filter((c) => c.itemId === projectId);
      }
      if (listFilter === 'incomplete') {
        cols = cols.filter((c) => columnIsIncomplete(c, statusOf));
      }
    } else if (filterTab === 'routine') {
      cols = cols.filter((c) => c.kind === 'routine');
      if (listFilter === 'incomplete') {
        cols = cols.filter((c) => columnIsIncomplete(c, statusOf));
      }
    } else if (filterTab === 'allIncomplete') {
      cols = cols.filter((c) => columnIsIncomplete(c, statusOf));
    }

    const regular = cols
      .filter((c) => c.kind === 'general')
      .sort((a, b) => (b.dueDate || '').localeCompare(a.dueDate || ''));
    const project = cols
      .filter((c) => c.kind === 'project')
      .sort((a, b) => {
        if (a.itemId !== b.itemId) {
          const ta = a.projectCreatedAt ? Date.parse(a.projectCreatedAt) : 0;
          const tb = b.projectCreatedAt ? Date.parse(b.projectCreatedAt) : 0;
          if (tb !== ta) return tb - ta;
          return a.itemId.localeCompare(b.itemId);
        }
        return (b.subTaskOrder ?? 0) - (a.subTaskOrder ?? 0);
      });
    const routine = cols
      .filter((c) => c.kind === 'routine')
      .sort((a, b) => (b.dueDate || '').localeCompare(a.dueDate || ''));

    return [...regular, ...project, ...routine];
  }, [
    allBaseColumns,
    categories,
    categoryId,
    doc.items,
    filterTab,
    groupIdSet,
    listFilter,
    projectId,
    resolveBaseStatus,
    routineDate,
  ]);

  const applySelectedStatus = useCallback(
    (newStatus: AssignmentStatusValue) => {
      if (selectedCells.size === 0) return;
      setPendingChanges((prev) => {
        const next = new Map(prev);
        selectedCells.forEach((ck) => {
          const [memberId, colKey] = ck.split('::');
          const col = columns.find((c) => c.key === colKey);
          if (!col || !col.assignedMemberIds.includes(memberId)) return;
          const existing = resolveBaseStatus(memberId, col);
          const points = pointsForStatus(col.basePoints, newStatus, doc.settings);
          next.set(ck, {
            status: newStatus,
            points,
            memo: existing?.memo,
          });
        });
        return next;
      });
      setSelectedCells(new Set());
    },
    [columns, doc.settings, resolveBaseStatus, selectedCells]
  );

  applyStatusRef.current = applySelectedStatus;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT'
      ) {
        return;
      }
      if (selectedCells.size === 0) return;
      const map: Record<string, AssignmentStatusValue> = {
        '1': 'pending',
        '2': 'confirmed',
        '3': 'delayed',
        '4': 'exempted',
      };
      const status = map[e.key];
      if (!status) return;
      e.preventDefault();
      applyStatusRef.current(status);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, selectedCells.size]);

  const toggleCell = (memberId: string, col: TableColumn) => {
    if (!col.assignedMemberIds.includes(memberId)) return;
    const ck = cellKey(memberId, col.key);
    setSelectedCells((prev) => {
      const next = new Set(prev);
      if (next.has(ck)) next.delete(ck);
      else next.add(ck);
      return next;
    });
  };

  const toggleColumn = (col: TableColumn) => {
    setSelectedCells((prev) => {
      const keys = col.assignedMemberIds.map((id) => cellKey(id, col.key));
      const allSelected = keys.length > 0 && keys.every((k) => prev.has(k));
      const next = new Set(prev);
      if (allSelected) {
        keys.forEach((k) => next.delete(k));
      } else {
        keys.forEach((k) => next.add(k));
      }
      return next;
    });
  };

  const requestClose = () => {
    if (pendingRef.current.size > 0) {
      setUnsavedConfirm(true);
      return;
    }
    onClose();
  };

  const resolveColFromKey = useCallback(
    (colKey: string): TableColumn | undefined => {
      const found =
        columns.find((c) => c.key === colKey) ??
        allBaseColumns.find((c) => c.key === colKey);
      if (found) return found;
      if (colKey.startsWith('g:')) {
        const itemId = colKey.slice(2);
        const item = doc.items.find((x) => x.id === itemId);
        if (item?.kind === 'general') {
          return {
            key: colKey,
            kind: 'general',
            itemId,
            title: item.title,
            dueDate: item.dueDate,
            color: '#a2a2a2',
            basePoints: item.basePoints,
            assignedMemberIds: item.assignedMemberIds,
          };
        }
      }
      if (colKey.startsWith('p:')) {
        const [, itemId, subTaskId] = colKey.split(':');
        const item = doc.items.find((x) => x.id === itemId);
        if (item?.kind === 'project' && subTaskId) {
          const sub = item.subTasks.find((s) => s.id === subTaskId);
          return {
            key: colKey,
            kind: 'project',
            itemId,
            subTaskId,
            title: sub?.title || '',
            dueDate: item.dueDate,
            color: item.color,
            basePoints: sub?.points ?? 0,
            assignedMemberIds: item.assignedMemberIds,
          };
        }
      }
      if (colKey.startsWith('r:')) {
        const [, itemId, date] = colKey.split(':');
        const item = doc.items.find((x) => x.id === itemId);
        if (item?.kind === 'routine' && date) {
          return {
            key: colKey,
            kind: 'routine',
            itemId,
            date,
            title: item.title,
            dueDate: date,
            color:
              (item.categoryId
                ? categories.find((c) => c.id === item.categoryId)?.color
                : undefined) || INCOMPLETE_ROUTINE_BADGE_BG,
            basePoints: item.basePoints,
            assignedMemberIds: item.assignedMemberIds,
          };
        }
      }
      return undefined;
    },
    [allBaseColumns, categories, columns, doc.items]
  );

  const handleSave = async () => {
    if (pendingChanges.size === 0 || saving) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const touchedIds = new Set<string>();

      type Patch = {
        memberId: string;
        change: PendingChange;
        subTaskId?: string;
        date?: string;
      };
      const generalPatches = new Map<string, Patch[]>();
      const projectPatches = new Map<string, Patch[]>();
      const routinePatches = new Map<string, Patch[]>();

      pendingChanges.forEach((change, ck) => {
        const [memberId, colKey] = ck.split('::');
        const col = resolveColFromKey(colKey);
        if (!col) return;
        touchedIds.add(col.itemId);
        const patch: Patch = {
          memberId,
          change,
          subTaskId: col.subTaskId,
          date: col.date,
        };
        if (col.kind === 'general') {
          const list = generalPatches.get(col.itemId) ?? [];
          list.push(patch);
          generalPatches.set(col.itemId, list);
        } else if (col.kind === 'project') {
          const list = projectPatches.get(col.itemId) ?? [];
          list.push(patch);
          projectPatches.set(col.itemId, list);
        } else {
          const list = routinePatches.get(col.itemId) ?? [];
          list.push(patch);
          routinePatches.set(col.itemId, list);
        }
      });

      const nextDoc: AssignmentPointsNoteDoc = {
        ...doc,
        updatedAt: now,
        items: doc.items.map((item) => {
          if (item.kind === 'general') {
            const patches = generalPatches.get(item.id);
            if (!patches?.length) return item;
            let statuses = [...item.studentStatuses];
            for (const { memberId, change } of patches) {
              const prev = statuses.find((s) => s.studentId === memberId);
              statuses = upsertStatus(statuses, {
                ...prev,
                studentId: memberId,
                status: change.status,
                earnedPoints: change.points,
                memo: change.memo ?? prev?.memo,
                updatedAt: now,
              });
            }
            return { ...item, studentStatuses: statuses, updatedAt: now };
          }
          if (item.kind === 'project') {
            const patches = projectPatches.get(item.id);
            if (!patches?.length) return item;
            const nextMap = { ...item.studentStatusesBySubTask };
            for (const { memberId, change, subTaskId } of patches) {
              if (!subTaskId) continue;
              const prev = nextMap[`${subTaskId}:${memberId}`];
              nextMap[`${subTaskId}:${memberId}`] = {
                ...prev,
                studentId: memberId,
                status: change.status,
                earnedPoints: change.points,
                memo: change.memo ?? prev?.memo,
                updatedAt: now,
              };
            }
            return { ...item, studentStatusesBySubTask: nextMap, updatedAt: now };
          }
          if (item.kind === 'routine') {
            const patches = routinePatches.get(item.id);
            if (!patches?.length) return item;
            let dateStatuses = [...ensureRoutineDateBuckets(item).dateStatuses];
            for (const { memberId, change, date } of patches) {
              if (!date) continue;
              const idx = dateStatuses.findIndex((b) => b.date === date);
              const prevBucket =
                idx >= 0
                  ? dateStatuses[idx]
                  : { date, studentStatuses: [] as AssignmentStudentStatus[] };
              const prevSt = prevBucket.studentStatuses.find(
                (s) => s.studentId === memberId
              );
              const statuses = upsertStatus(prevBucket.studentStatuses, {
                ...prevSt,
                studentId: memberId,
                status: change.status,
                earnedPoints: change.points,
                memo: change.memo ?? prevSt?.memo,
                updatedAt: now,
              });
              const bucket = { ...prevBucket, date, studentStatuses: statuses };
              if (idx >= 0) dateStatuses[idx] = bucket;
              else dateStatuses = [...dateStatuses, bucket];
            }
            return { ...item, dateStatuses, updatedAt: now };
          }
          return item;
        }),
      };

      await onSave(nextDoc, Array.from(touchedIds));
      setPendingChanges(new Map());
      setSelectedCells(new Set());
    } finally {
      setSaving(false);
    }
  };

  const filterSummary = (() => {
    const status = listFilter === 'all' ? '전체 과제' : '미완료 과제';
    if (filterTab === 'allIncomplete') return status;
    if (filterTab === 'general') {
      const cat =
        categoryId === 'all'
          ? '전체 카테고리'
          : categories.find((c) => c.id === categoryId)?.name || '카테고리';
      return `${status} · ${cat}`;
    }
    if (filterTab === 'project') {
      const proj =
        projectId === 'all'
          ? '모든 프로젝트'
          : projectOptions.find((p) => p.id === projectId)?.title || '프로젝트';
      return `${status} · ${proj}`;
    }
    return `${status} · ${formatDateMenuLabel(routineDate)}`;
  })();

  const filterDotColor = (() => {
    if (filterTab === 'general') {
      if (categoryId === 'all') return 'rgba(162, 162, 162, 1)';
      return categories.find((c) => c.id === categoryId)?.color || 'rgba(162, 162, 162, 1)';
    }
    if (filterTab === 'routine') return INCOMPLETE_ROUTINE_BADGE_BG;
    return 'rgba(162, 162, 162, 1)';
  })();

  if (!open) return null;

  const gridTemplate = `40px 60px repeat(${Math.max(columns.length, 1)}, 80px)`;

  return (
    <div className="cp-apn-tbl-overlay" role="presentation" onClick={requestClose}>
      <div
        className="cp-apn-tbl-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-apn-tbl-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-apn-tbl-header">
          <h2 id="cp-apn-tbl-title">과제 관리 테이블</h2>
          {filterTab !== 'allIncomplete' ? (
            <div className="cp-apn-list-filter cp-apn-tbl-header-filter">
              <button
                type="button"
                className="cp-apn-list-filter-btn"
                aria-expanded={filterMenuOpen}
                onClick={() => setFilterMenuOpen((v) => !v)}
              >
                <span
                  className="cp-apn-list-filter-dot"
                  style={{ background: filterDotColor }}
                  aria-hidden
                />
                <span>{filterSummary}</span>
              </button>
              {filterMenuOpen ? (
                <>
                  <div
                    className="cp-apn-list-filter-overlay"
                    role="presentation"
                    onClick={() => setFilterMenuOpen(false)}
                  />
                  <div
                    className="cp-apn-list-filter-menu cp-apn-tbl-header-filter-menu"
                    role="listbox"
                  >
                    <button
                      type="button"
                      className={`cp-apn-list-filter-option${
                        listFilter === 'all' ? ' is-selected' : ''
                      }`}
                      onClick={() => {
                        setListFilter('all');
                        setFilterMenuOpen(false);
                      }}
                    >
                      전체 과제
                    </button>
                    <button
                      type="button"
                      className={`cp-apn-list-filter-option${
                        listFilter === 'incomplete' ? ' is-selected' : ''
                      }`}
                      onClick={() => {
                        setListFilter('incomplete');
                        setFilterMenuOpen(false);
                      }}
                    >
                      미완료 과제
                    </button>
                    {filterTab === 'general' ? (
                      <>
                        <div className="cp-apn-list-filter-divider" />
                        <button
                          type="button"
                          className={`cp-apn-list-filter-option${
                            categoryId === 'all' ? ' is-selected' : ''
                          }`}
                          onClick={() => {
                            setCategoryId('all');
                            setFilterMenuOpen(false);
                          }}
                        >
                          <span
                            className="cp-apn-list-filter-dot"
                            style={{ background: 'rgba(162, 162, 162, 1)' }}
                            aria-hidden
                          />
                          전체 카테고리
                        </button>
                        {categories.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className={`cp-apn-list-filter-option${
                              categoryId === c.id ? ' is-selected' : ''
                            }`}
                            onClick={() => {
                              setCategoryId(c.id);
                              setFilterMenuOpen(false);
                            }}
                          >
                            <span
                              className="cp-apn-list-filter-dot"
                              style={{ background: c.color }}
                              aria-hidden
                            />
                            {c.name}
                          </button>
                        ))}
                      </>
                    ) : null}
                    {filterTab === 'project' ? (
                      <>
                        <div className="cp-apn-list-filter-divider" />
                        <button
                          type="button"
                          className={`cp-apn-list-filter-option${
                            projectId === 'all' ? ' is-selected' : ''
                          }`}
                          onClick={() => {
                            setProjectId('all');
                            setFilterMenuOpen(false);
                          }}
                        >
                          <span className="cp-apn-list-filter-dot" aria-hidden />
                          모든 프로젝트
                        </button>
                        {projectOptions.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            className={`cp-apn-list-filter-option${
                              projectId === p.id ? ' is-selected' : ''
                            }`}
                            onClick={() => {
                              setProjectId(p.id);
                              setFilterMenuOpen(false);
                            }}
                          >
                            <span className="cp-apn-list-filter-dot" aria-hidden />
                            {p.title}
                          </button>
                        ))}
                      </>
                    ) : null}
                    {filterTab === 'routine' ? (
                      <>
                        <div className="cp-apn-list-filter-divider" />
                        {routineDateOptions.map((iso) => (
                          <button
                            key={iso}
                            type="button"
                            className={`cp-apn-list-filter-option${
                              routineDate === iso ? ' is-selected' : ''
                            }`}
                            onClick={() => {
                              setRoutineDate(iso);
                              setFilterMenuOpen(false);
                            }}
                          >
                            <span className="cp-apn-list-filter-dot" aria-hidden />
                            {formatDateMenuLabel(iso)}
                          </button>
                        ))}
                      </>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            className="cp-apn-tbl-close"
            aria-label="모달 닫기"
            onClick={requestClose}
          >
            ×
          </button>
        </header>

        <div className="cp-apn-tbl-tabs" role="tablist" aria-label="과제 유형">
          {(
            [
              ['allIncomplete', '미완료 전체'],
              ['general', '일반 과제'],
              ['project', '프로젝트 과제'],
              ['routine', '루틴 과제'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={filterTab === key}
              className={`cp-apn-tbl-tab${filterTab === key ? ' is-active' : ''}`}
              onClick={() => {
                setFilterTab(key);
                setFilterMenuOpen(false);
                setSelectedCells(new Set());
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="cp-apn-tbl-body">
          <div className="cp-apn-tbl-toolbar">
            <div className="cp-apn-tbl-legend" role="group" aria-label="상태 변경">
              {STATUS_LEGEND.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className="cp-apn-tbl-legend-btn"
                  disabled={selectedCells.size === 0}
                  onClick={() => applySelectedStatus(opt.value)}
                >
                  <span
                    className="cp-apn-tbl-legend-num"
                    data-status={opt.value}
                    style={{ background: opt.color }}
                  >
                    {opt.num}
                  </span>
                  <span className="cp-apn-tbl-legend-label">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {members.length === 0 ? (
            <div className="cp-apn-tbl-empty">멤버 데이터가 없습니다.</div>
          ) : columns.length === 0 ? (
            <div className="cp-apn-tbl-empty">
              {filterTab === 'allIncomplete'
                ? '미완료 과제가 없습니다.'
                : filterTab === 'routine'
                  ? '해당 날짜에 진행하는 루틴 과제가 없습니다.'
                  : listFilter === 'incomplete'
                    ? '미완료 과제가 없습니다.'
                    : '과제가 없습니다.'}
            </div>
          ) : (
            <div className="cp-apn-tbl-scroll">
              <div
                className="cp-apn-tbl-grid"
                style={{ gridTemplateColumns: gridTemplate }}
              >
                <div className="cp-apn-tbl-corner cp-apn-tbl-sticky-num">번호</div>
                <div className="cp-apn-tbl-corner cp-apn-tbl-sticky-name">이름</div>

                {columns.map((col) => {
                  const fd = formatHeaderDate(col.dueDate);
                  return (
                    <div
                      key={`d-${col.key}`}
                      className="cp-apn-tbl-due"
                      style={{ ['--cp-apn-tbl-bar' as string]: col.color }}
                    >
                      {fd ? (
                        <>
                          <span>{fd.md}</span>
                          <span>({fd.weekday})</span>
                        </>
                      ) : (
                        '—'
                      )}
                    </div>
                  );
                })}

                {columns.map((col) => (
                  <button
                    key={`t-${col.key}`}
                    type="button"
                    className="cp-apn-tbl-title"
                    title={
                      col.kind === 'project'
                        ? `${col.projectTitle} / ${col.subTaskTitle}`
                        : col.title
                    }
                    onClick={() => toggleColumn(col)}
                  >
                    {col.kind === 'project' ? (
                      <>
                        <span className="cp-apn-tbl-title-main">{col.projectTitle}</span>
                        <span className="cp-apn-tbl-title-sub">{col.subTaskTitle}</span>
                      </>
                    ) : (
                      col.title
                    )}
                  </button>
                ))}

                {members.map((m) => (
                  <React.Fragment key={m.id}>
                    <div className="cp-apn-tbl-num cp-apn-tbl-sticky-num">
                      {m.orderIndex}번
                    </div>
                    <div className="cp-apn-tbl-name cp-apn-tbl-sticky-name" title={displayClassMemberName(m)}>
                      {displayClassMemberName(m)}
                    </div>
                    {columns.map((col) => {
                      const assigned = col.assignedMemberIds.includes(m.id);
                      const ck = cellKey(m.id, col.key);
                      const status = resolveDisplayStatus(m.id, col);
                      const selected = selectedCells.has(ck);
                      const pending = pendingChanges.has(ck);
                      return (
                        <button
                          key={ck}
                          type="button"
                          className={`cp-apn-tbl-cell${assigned ? '' : ' is-unassigned'}${
                            selected ? ' is-selected' : ''
                          }${pending ? ' is-pending' : ''}`}
                          style={
                            assigned
                              ? {
                                  background:
                                    STATUS_LEGEND.find((s) => s.value === status)?.color ??
                                    '#FF316B',
                                  color: statusFg(status),
                                }
                              : undefined
                          }
                          disabled={!assigned}
                          onClick={() => toggleCell(m.id, col)}
                          aria-label={`${displayClassMemberName(m)} ${col.title} ${assigned ? status : '미부여'}`}
                        >
                          {assigned ? statusIcon(status) : null}
                        </button>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          className={`cp-apn-tbl-fab${hasChanges && !saving ? '' : ' is-disabled'}`}
          disabled={!hasChanges || saving}
          onClick={() => void handleSave()}
          aria-label="저장하기"
          title="저장하기"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16L21 8V19C21 20.1046 20.1046 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <UnsavedCloseConfirm
          open={unsavedConfirm}
          onCancel={() => setUnsavedConfirm(false)}
          onConfirm={() => {
            setUnsavedConfirm(false);
            setPendingChanges(new Map());
            onClose();
          }}
        />
      </div>
    </div>
  );
};

export default AssignmentManagementTableModal;
